import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLogin } from '../hooks/useAuthQueries';
import { ROUTES } from '../utils/constants';
import { getRedirectPath } from '../utils/roleUtils';
import logo from '../assets/logo.svg';
import './Login.css';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const loginMutation = useLogin();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  // Handle login success
  useEffect(() => {
    if (loginMutation.isSuccess && loginMutation.data) {
      const { access, refresh, user } = loginMutation.data;
      
      if (access && user) {
        // Store tokens and user data
        login(user, access, refresh);
        // Redirect based on role and verification status
        const redirectPath = getRedirectPath(user);
        navigate(redirectPath);
      }
    }
  }, [loginMutation.isSuccess, loginMutation.data, login, navigate]);

  // Handle login errors
  useEffect(() => {
    if (loginMutation.isError) {
      const errorData = loginMutation.error?.data;
      if (errorData?.detail) {
        setError(errorData.detail);
      } else if (errorData) {
        // Handle field-specific errors
        const errorMessages = [];
        Object.keys(errorData).forEach(key => {
          const messages = Array.isArray(errorData[key]) ? errorData[key] : [errorData[key]];
          errorMessages.push(...messages);
        });
        setError(errorMessages.join('. ') || 'Login failed. Please check your credentials.');
      } else {
        setError('Login failed. Please check your credentials.');
      }
    }
  }, [loginMutation.isError, loginMutation.error]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      await loginMutation.mutateAsync(formData);
    } catch (err) {
      // Error handling is done in useEffect above
      console.error('[Login] Error:', err);
    }
  };

  return (
    <div className="login">
      <div className="login__container">
        {/* Left Side - Login Form */}
        <div className="login__form-section">
          <div className="login__form-wrapper">
            {/* Logo */}
            <div className="login__brand">
              <img src={logo} alt="Sage Locum Logo" className="login__logo" />
            </div>

            {/* Welcome Message */}
            <div className="login__welcome">
              <h2 className="login__welcome-title">Welcome Back</h2>
              <p className="login__welcome-subtitle">
                Sign in to continue to your account
              </p>
            </div>

            {/* Login Form */}
            <form className="login__form" onSubmit={handleSubmit}>
              {error && (
                <div className="login__error" role="alert">
                  <svg className="login__error-icon" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  {error}
                </div>
              )}

              <div className="login__field">
                <label htmlFor="email" className="login__label">Email Address</label>
                <div className="login__input-wrapper">
                  <svg className="login__input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                  </svg>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="login__input"
                    placeholder="Enter your email"
                    required
                    disabled={loginMutation.isPending}
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="login__field">
                <label htmlFor="password" className="login__label">Password</label>
                <div className="login__input-wrapper">
                  <svg className="login__input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="login__input"
                    placeholder="Enter your password"
                    required
                    disabled={loginMutation.isPending}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="login__password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

             

              <button
                type="submit"
                className="login__button"
                disabled={loginMutation.isPending}
              >
                {loginMutation.isPending ? (
                  <>
                    <svg className="login__button-spinner" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>

            {/* Signup Link */}
            <div className="login__signup">
              <p className="login__signup-text">
                Don't have an account?{' '}
                <Link to={ROUTES.SIGNUP} className="login__signup-link">
                  Sign up here
                </Link>
              </p>
            </div>
          </div>
        </div>

        {/* Right Side - Visual/Illustration */}
        <div className="login__visual-section">
          <div className="login__visual-content">
            <div className="login__visual-icon">
              <svg viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Dental Smile Illustration */}
                <defs>
                  <linearGradient id="dentalGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.2" />
                    <stop offset="100%" stopColor="#ffffff" stopOpacity="0.05" />
                  </linearGradient>
                  <linearGradient id="toothGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" />
                    <stop offset="100%" stopColor="#ffffff" stopOpacity="0.7" />
                  </linearGradient>
                </defs>
                
                {/* Background circle */}
                <circle cx="120" cy="120" r="100" fill="url(#dentalGradient)"/>
                
                {/* Smile arc */}
                <path d="M 60 140 Q 120 180 180 140" stroke="white" strokeWidth="8" strokeLinecap="round" fill="none" opacity="0.9"/>
                
                {/* Teeth */}
                <ellipse cx="90" cy="130" rx="12" ry="18" fill="url(#toothGradient)"/>
                <ellipse cx="110" cy="125" rx="12" ry="20" fill="url(#toothGradient)"/>
                <ellipse cx="130" cy="125" rx="12" ry="20" fill="url(#toothGradient)"/>
                <ellipse cx="150" cy="130" rx="12" ry="18" fill="url(#toothGradient)"/>
                
                {/* Medical cross symbol */}
                <g transform="translate(120, 80)">
                  <rect x="-3" y="-15" width="6" height="30" fill="white" opacity="0.8" rx="2"/>
                  <rect x="-15" y="-3" width="30" height="6" fill="white" opacity="0.8" rx="2"/>
                </g>
              </svg>
            </div>
            <h3 className="login__visual-title">Your Dental Career, Simplified</h3>
            <p className="login__visual-description">
              Connect with dental practices across the UK. Find flexible locum opportunities that fit your schedule and advance your career.
            </p>
            <div className="login__features">
              <div className="login__feature">
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>GDC Verified Professionals</span>
              </div>
              <div className="login__feature">
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
                <span>Flexible Work Schedule</span>
              </div>
              <div className="login__feature">
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Secure & Trusted Platform</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
